import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import Table from "../../../core/common/dataTable/index";
import Toasts from "../../../core/common/toast";
import { all_routes } from "../../router/all_routes";
import Shifts from "../reportingEmployees/shifts";

import { getTeamShiftHistory } from "../../../core/data/redux/reportingEmployeesSlice";
import { DateFormat } from "../../../core/hooks/dateFormat";

interface ReportingShiftProps {
  employee?: any; // ✅ Make optional
}

const ReportingShift: React.FC<ReportingShiftProps> = ({ employee }) => {
  const location = useLocation();
  const stateEmployee = location.state?.employee;

  const resolvedEmployee = employee || stateEmployee;
  const empId = resolvedEmployee?.empId;

  const [currentItem, setCurrentItem] = useState<any>(resolvedEmployee || null);

  const reportingEmployeesState = useSelector(
    (state: any) => state.reportingEmployeesState
  );

  const [dataList, setDataList] = useState<any[]>([]);
  const [query, setQuery] = useState<string>("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 10,
  });

  const dispatch = useDispatch();

  function debounce(func: (...args: any[]) => void, delay: number) {
    let timer: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        func(...args);
      }, delay);
    };
  }

  const fetchRecords = (
    pagination = { current: 1, pageSize: 10 },
    empId: string = ""
  ) => {
    dispatch(
      getTeamShiftHistory({
        page: pagination.current,
        limit: pagination.pageSize,
        empId,
      })
    );
  };

  const debouncedSearch = useRef(
    debounce((value: string) => {
      const trimmed = value.trim();
      if (trimmed === "") {
        setQuery("");
        fetchRecords(pagination, empId);
        return;
      }
      if (trimmed.length < 4) return;
      setQuery(trimmed);
      fetchRecords(pagination, trimmed);
    }, 500)
  ).current;

  const handleSearch = (value: string) => {
    debouncedSearch(value);
  };

  useEffect(() => {
    if (empId) {
      fetchRecords(pagination, empId);
    }
  }, [empId]);

  useEffect(() => {
    if (
      reportingEmployeesState.loading === "fulfilled" &&
      reportingEmployeesState.action === "assign"
    ) {
      fetchRecords(pagination, empId);
    } else if (
      reportingEmployeesState.loading === "fulfilled" &&
      reportingEmployeesState.action === "list"
    ) {
      const result = reportingEmployeesState?.data?.data || null;
      if (result?.data && result.data.length > 0) {
        const employees = result.data;
        setDataList(employees);
        setPagination({
          ...pagination,
          total: result.total || employees.length,
          current: result.page || 1,
          pageSize: result.limit || 10,
        });
      } else {
        setDataList([]);
      }
    }
  }, [reportingEmployeesState.loading]);

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      PRESENT: "#049123",
      ABSENT: "#C0282C",
      HALF_DAY: "#FFA500",
      LATE_COMING: "#B09BFB",
      EARLY_LEAVING: "#FFFF00",
      HOLIDAY: "#969696",
    };
    return colors[status] || "#ccc";
  };

  const attendanceColumns = [
    {
      title: "Sr. No.",
      dataIndex: "id",
      width: 80,
      render: (_: any, __: any, index: number) =>
        index + 1 + (pagination.current - 1) * pagination.pageSize,
    },
    {
      title: "Emp ID",
      dataIndex: "empId",
      render: (empId: string) => parseInt(empId, 10),
    },
    {
      title: "Shift Name",
      dataIndex: "work_shift",
      render: (text: string, record: any) =>
        record?.work_shift?.shift_name || "-",
    },
    {
      title: "Start Time",
      dataIndex: "work_shift",
      render: (text: string, record: any) => record?.work_shift?.start_time,
    },
    {
      title: "End Time",
      dataIndex: "work_shift",
      render: (text: string, record: any) => record?.work_shift?.end_time,
    },
    {
      title: "Break Time (minute)",
      dataIndex: "work_shift",
      render: (text: string, record: any) =>
        record?.work_shift?.break_time_in_minutes + " min",
    },
    {
      title: "Shift Assigned On",
      dataIndex: "created_at",
      render: (text: string, record: any) => DateFormat(record?.created_at),
    },
    // {
    //   title: "Is Verified",
    //   dataIndex: "is_verified",
    //   sorter: (a: any, b: any) => a.is_verified.length - b.is_verified.length,
    //   render: (is_verified: any, record: any) => {
    //     return (
    //       <span style={{ minWidth: 100, padding: 2, textAlign: 'center', cursor: 'pointer' }} className={`badge ${(is_verified == 'VERIFIED') ? 'badge-success' : 'badge-danger'}  align-items-center badge-xs`}>
    //         {is_verified}
    //       </span>
    //     )
    //   },
    // },
    {
      title: "Is Active",
      dataIndex: "is_archived",
      sorter: (a: any, b: any) => a.is_verified.length - b.is_verified.length,
      render: (is_archived: any, record: any) => {
        return (
          <span
            style={{
              minWidth: 60,
              padding: 2,
              textAlign: "center",
              cursor: "pointer",
            }}
            className={`badge ${
              is_archived ? "badge-danger" : "badge-success"
            }  align-items-center badge-xs`}
          >
            {is_archived ? "Inactive" : "Active"}
          </span>
        );
      },
    },
  ];

  const handlePagination = (pagination: any) => {
    setPagination({
      current: pagination.page,
      pageSize: pagination.pageSize,
      total: pagination.total || 10,
    });
    fetchRecords(pagination, empId);
  };

  return (
    <div>
      <Toasts
        show={reportingEmployeesState.loading === "rejected"}
        message={reportingEmployeesState.error}
        title={"Failed"}
        type={"danger"}
      />
      <Toasts
        show={
          reportingEmployeesState.loading === "fulfilled" &&
          reportingEmployeesState.action !== "list"
        }
        message={reportingEmployeesState?.data?.message}
        title={"Success"}
        type={"success"}
      />
      <div className="page-wrapper">
        <div className="content">
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Shift</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="#">Employees</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="#">My Team</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Shift History
                  </li>
                </ol>
              </nav>
            </div>
          </div>

          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-4 mt-3">Employee Shift History</h4>
              <button
                className="btn btn-primary d-flex align-items-center gap-2"
                data-bs-toggle="modal"
                data-bs-target="#assign_office_modal"
                onClick={() => setCurrentItem(resolvedEmployee)}
                style={{
                  //   backgroundColor: "#f97316",
                  color: "#fff",
                  border: "none",
                  padding: "8px 16px",
                  borderRadius: "0.5rem",
                  fontWeight: 500,
                  fontSize: "14px",
                }}
              >
                Assign or Change Shift
              </button>
            </div>
            <div className="card-body p-0 py-3">
              <Table
                columns={attendanceColumns}
                dataSource={dataList}
                Selection={false}
                paginationConfig={pagination}
                handlePagination={handlePagination}
                loading={reportingEmployeesState.loading === "pending"}
                handleSearch={handleSearch}
              />
            </div>
          </div>

          <div
            className="modal fade"
            id="assign_office_modal"
            tabIndex={-1}
            role="dialog"
            aria-labelledby="assignOfficeModalLabel"
            aria-hidden="true"
          >
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <div className="d-flex align-items-center">
                    <h4 className="modal-title me-2">Assign Shift</h4>
                    <span>Employee ID : {parseInt(empId, 10)}</span>
                  </div>
                  <button
                    type="button"
                    className="btn-close custom-btn-close"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                  >
                    <i className="ti ti-x" />
                  </button>
                </div>
                <div className="modal-body">
                  <Shifts employee={resolvedEmployee} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportingShift;
